import { useEffect, useState, type FC } from 'react';

import {
  AutoTruncateText,
  checkEmpty,
  Col,
  Container,
  Drawer,
  HighlightText,
  isTextTruncated,
  NoDataContent,
  Row,
  Search,
  Table,
  Tooltip,
  truncateText,
  Typography,
} from 'pixel-react';

import {
  LICENSE_TYPE,
  MACHINE_DETAILS_KEY,
  USER_DETAILS_KEY,
} from '@/components/ExecutionDetails/constants';
import type {
  MachineOrUserDetailsDrawerProps,
  MachineTableData,
  UserTableData,
} from '@/components/ExecutionDetails/types';

import styles from './ExecutionDetailsDrawer.module.scss';
const MachineOrUserDetailsDrawer: FC<MachineOrUserDetailsDrawerProps> = ({
  onClose,
  type,
  data,
  tableData,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dataList, setDataList] = useState<
    MachineTableData[] | UserTableData[]
  >([]);
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const columnData = [
    {
      header: 'Execution Id',
      accessor: 'executionId',
      width: 106,
      cell: (e: { value: string }) => {
        return (
          <div className={styles.execution_icon}>
            <HighlightText highlight={searchTerm} text={e?.value} />
          </div>
        );
      },
    },
    {
      header: 'Execution Name',
      accessor: 'executionName',
      width: 221,
      cell: (e: { value: string }) => {
        return (
          <AutoTruncateText>
            <HighlightText highlight={searchTerm} text={e?.value} />
          </AutoTruncateText>
        );
      },
    },
    {
      header: 'Project',
      accessor: 'project',
      width: 180,
      cell: (e: { value: string }) => {
        return (
          <AutoTruncateText>
            <HighlightText highlight={searchTerm} text={e?.value} />
          </AutoTruncateText>
        );
      },
    },
  ];
  const newColumnData =
    type === 'Machine'
      ? [
          {
            header: 'Initiated By',
            accessor: 'initiatedBy',
            width: 120,
            cell: ({ value }: { value: string }) => (
              <AutoTruncateText>
                <HighlightText highlight={searchTerm} text={value} />
              </AutoTruncateText>
            ),
          },
          {
            header: 'Status',
            accessor: 'status',
            width: 120,
            cell: ({ value }: { value: string }) => (
              <AutoTruncateText>
                <HighlightText highlight={searchTerm} text={value} />
              </AutoTruncateText>
            ),
          },
        ]
      : [
          {
            header: 'Machine Name',
            accessor: 'machineName',
            width: 120,
            cell: ({ value }: { value: string }) => (
              <AutoTruncateText>
                <HighlightText highlight={searchTerm} text={value} />
              </AutoTruncateText>
            ),
          },
          {
            header: 'Status',
            accessor: 'status',
            width: 150,
            cell: ({ value }: { value: string }) => (
              <AutoTruncateText>
                <HighlightText highlight={searchTerm} text={value} />
              </AutoTruncateText>
            ),
          },
        ];
  columnData.push(...newColumnData);

  const DETAIL_KEYS =
    type === 'Machine' ? MACHINE_DETAILS_KEY : USER_DETAILS_KEY;
  const getData = (objKey: string) => {
    if (!data) return '---';
    for (const key of Object.keys(data) as (keyof typeof data)[]) {
      if (key === objKey) {
        return data[key];
      }
    }
    return '---';
  };
  const handleCloseSearch = () => {
    setIsExpand(false);
    setSearchTerm('');
  };

  useEffect(() => {
    if (searchTerm) {
      const searchData = searchTerm?.toLowerCase();
      const filteredLabelsList = tableData?.filter(
        data =>
          data.executionId.toLowerCase().includes(searchData) ||
          data?.executionName?.toLowerCase()?.includes(searchData) ||
          data?.project?.toLowerCase()?.includes(searchData) ||
          data?.initiatedBy?.toLowerCase()?.includes(searchData) ||
          data?.machineName?.toLowerCase()?.includes(searchData) ||
          data?.status?.toLowerCase()?.includes(searchData),
      );
      setDataList(filteredLabelsList as MachineTableData[] | UserTableData[]);
    } else {
      setDataList(tableData);
    }
  }, [searchTerm, tableData]);
  const isSearching = searchTerm.trim().length > 0;
  const hasNoResults = isSearching && checkEmpty(dataList);

  return (
    <Drawer
      isOpen
      isClickOutside
      isBackButtonVisible
      onBackButtonClick={onClose}
      title={`${type} Details - ${LICENSE_TYPE?.responseObject?.licenseName}`}
      onClose={onClose}
      size="medium"
      zIndex={9999}
      top="56px"
      overlay
      isFooterRequired={false}
    >
      <Container fluid className={styles.execution_container}>
        <Row gap="12px">
          {Object.entries(DETAIL_KEYS).map(([key, value], rowIndex) => (
            <Col key={rowIndex} size={3} className={styles.machine_user_table}>
              <Typography lineHeight="18px" fontWeight="medium" as="div">
                {value}
              </Typography>
              <Tooltip
                key={rowIndex}
                placement="bottom"
                title={
                  isTextTruncated(getData(key), 150, 'pixel')
                    ? getData(key)
                    : ''
                }
                zIndex={100003}
              >
                <Typography
                  color={
                    value.toLocaleLowerCase() !== 'status'
                      ? 'var(--description-text)'
                      : getData(key)
                        ? 'var(--confirm-tick-icon-color)'
                        : 'var(--status-button-text-failed)'
                  }
                  as="div"
                  lineHeight="18px"
                  className={styles.machine_user_details}
                  children={truncateText(getData(key), 150, 'pixel')}
                />
              </Tooltip>
            </Col>
          ))}
        </Row>
        <Row>
          <Col>
            <div className={styles.search_container}>
              <Typography children={'Execution Details'} fontWeight="bold" />
              <div className={styles.search_icon}>
                <Search
                  placeholder="Search"
                  isExpand={isExpand}
                  value={searchTerm}
                  onSearch={setSearchTerm}
                  onExpand={(expand: boolean) => setIsExpand(expand)}
                  onClose={handleCloseSearch}
                  disabled={false}
                />
              </div>
            </div>
          </Col>
          <Col
            className={
              type === 'Machine'
                ? styles.user_table_container
                : styles.machine_table_container
            }
          >
            <Table
              data={dataList}
              columns={columnData}
              noDataContent={
                hasNoResults ? (
                  <NoDataContent
                    iconName={'no_license_found'}
                    text={'No Result found'}
                  />
                ) : (
                  <NoDataContent
                    iconName="no_content"
                    text="No Data Available"
                    textFontSize={'20px'}
                  />
                )
              }
              headerType="secondary"
              tableDataTextColor="var(--table-header-text-color)"
            />
          </Col>
        </Row>
      </Container>
    </Drawer>
  );
};
export default MachineOrUserDetailsDrawer;
